import React from 'react';
import { useTranslation } from 'react-i18next';
import { Repeat, RefreshCw, CheckCircle2, Power } from 'lucide-react';

export function AutoInvestTab({
  aiConfig, setAiConfig, aiStrategies, aiFrequencies, agents,
  onSave, onRebalance, aiSaving, aiRebalancing, aiHistory,
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6" data-testid="autoinvest-section">
      <div className="bg-[#090B0F] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Repeat className="w-6 h-6 text-emerald-500" />
            <div>
              <h2 className="text-lg font-bold text-white">Auto-Invest</h2>
              <p className="text-slate-400 text-xs">{t('wallet.aiAutoRedistribute')}</p>
            </div>
          </div>
          <button
            onClick={() => setAiConfig({ ...aiConfig, enabled: !aiConfig.enabled })}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-bold transition-all ${
              aiConfig.enabled ? 'bg-emerald-500 text-[#030508] hover:bg-emerald-400' : 'bg-white/10 text-slate-400 hover:bg-white/20'
            }`}
            data-testid="autoinvest-toggle"
          >
            <Power className="w-4 h-4" />
            {aiConfig.enabled ? t('wallet.active') : t('wallet.inactive')}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-[#030508] border border-white/5 p-3">
            <p className="text-[10px] text-slate-500 font-mono uppercase">{t('wallet.rebalances')}</p>
            <p className="text-lg font-bold text-white">{aiConfig.total_rebalances || 0}</p>
          </div>
          <div className="bg-[#030508] border border-white/5 p-3">
            <p className="text-[10px] text-slate-500 font-mono uppercase">{t('wallet.autoInvested')}</p>
            <p className="text-lg font-bold text-emerald-400">${(aiConfig.total_auto_invested || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-[#030508] border border-white/5 p-3">
            <p className="text-[10px] text-slate-500 font-mono uppercase">{t('wallet.lastRebalance')}</p>
            <p className="text-sm font-mono text-white">{aiConfig.last_rebalance ? new Date(aiConfig.last_rebalance).toLocaleDateString() : t('wallet.never')}</p>
          </div>
        </div>
      </div>

      <div className="bg-[#090B0F] border border-white/10 p-6 space-y-4">
        <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('common.strategy')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {aiStrategies.map(s => (
            <button key={s.id}
              onClick={() => setAiConfig({ ...aiConfig, strategy: s.id })}
              className={`p-4 border text-left transition-all ${
                aiConfig.strategy === s.id ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-white/10 hover:border-white/20'
              }`}
              data-testid={`strategy-${s.id}`}
            >
              <div className="font-bold text-sm text-white mb-1">{s.name}</div>
              <div className="text-xs text-slate-400">{s.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#090B0F] border border-white/10 p-6 space-y-4">
        <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('wallet.configuration')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 font-mono uppercase block mb-2">{t('wallet.rebalanceFrequency')}</label>
            <div className="flex gap-2">
              {aiFrequencies.map(f => (
                <button key={f.id}
                  onClick={() => setAiConfig({ ...aiConfig, frequency: f.id })}
                  className={`px-3 py-2 text-xs font-mono transition-all ${
                    aiConfig.frequency === f.id ? 'bg-emerald-500 text-[#030508] font-bold' : 'bg-white/10 text-slate-400 hover:bg-white/20'
                  }`}
                  data-testid={`freq-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 font-mono uppercase block mb-2">{t('wallet.newCapitalPerCycle')}</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={aiConfig.amount_per_cycle || ''}
                onChange={e => setAiConfig({ ...aiConfig, amount_per_cycle: parseFloat(e.target.value) || 0 })}
                placeholder="0 = solo rebalancear"
                className="flex-1 bg-[#11141D] border border-white/10 text-white px-4 py-2 text-sm font-mono focus:outline-none focus:border-emerald-500/50"
                data-testid="autoinvest-amount-input"
              />
              <span className="text-xs text-slate-500 flex items-center">USD</span>
            </div>
            <p className="text-[10px] text-slate-600 mt-1">{t('wallet.zeroNote')}</p>
          </div>
        </div>
      </div>

      <div className="bg-[#090B0F] border border-white/10 p-6 space-y-4">
        <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('wallet.includedAgents')}</h3>
        <p className="text-xs text-slate-400">{t('wallet.basedOnProfile')} <span className="text-emerald-400 font-bold uppercase">{aiConfig.risk_profile || 'moderate'}</span></p>
        <div className="flex flex-wrap gap-2">
          {agents.map(a => {
            const selected = (aiConfig.custom_agents || []).includes(a);
            return (
              <button key={a}
                onClick={() => {
                  const current = aiConfig.custom_agents || [];
                  const updated = selected ? current.filter(x => x !== a) : [...current, a];
                  setAiConfig({ ...aiConfig, custom_agents: updated });
                }}
                className={`px-3 py-1.5 text-xs font-mono transition-all ${
                  selected ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-slate-500 border border-white/10 hover:border-white/20'
                }`}
                data-testid={`agent-toggle-${a}`}
              >
                {a}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex gap-3">
        <button onClick={onSave} disabled={aiSaving}
          className="flex items-center gap-2 px-6 py-3 bg-emerald-500 text-[#030508] font-bold text-sm hover:bg-emerald-400 transition-colors disabled:opacity-50"
          data-testid="save-autoinvest-btn">
          {aiSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {aiSaving ? t('wallet.saving') : t('wallet.saveConfig')}
        </button>
        <button onClick={onRebalance} disabled={aiRebalancing}
          className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-bold text-sm hover:bg-white/20 transition-colors disabled:opacity-50"
          data-testid="manual-rebalance-btn">
          {aiRebalancing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Repeat className="w-4 h-4" />}
          {aiRebalancing ? t('wallet.rebalancing') : t('wallet.rebalanceNow')}
        </button>
      </div>

      {aiHistory.length > 0 && (
        <div className="bg-[#090B0F] border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('wallet.rebalanceHistory')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-slate-500 text-xs font-mono uppercase">
                  <th className="text-left p-3">{t('common.date')}</th>
                  <th className="text-left p-3">{t('common.strategy')}</th>
                  <th className="text-right p-3">{t('wallet.newCapital')}</th>
                  <th className="text-right p-3">{t('wallet.totalCapital')}</th>
                  <th className="text-right p-3">{t('wallet.movements')}</th>
                </tr>
              </thead>
              <tbody>
                {aiHistory.map((h, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 text-white text-xs font-mono">{new Date(h.created_at).toLocaleString()}</td>
                    <td className="p-3"><span className="px-2 py-0.5 text-xs font-mono bg-emerald-500/20 text-emerald-400">{h.strategy}</span></td>
                    <td className="p-3 text-right text-emerald-400 font-mono">{h.new_capital_added > 0 ? `+$${h.new_capital_added.toLocaleString()}` : '-'}</td>
                    <td className="p-3 text-right text-white font-mono">${h.total_capital?.toLocaleString()}</td>
                    <td className="p-3 text-right text-slate-400 font-mono">{h.moves?.length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
